import { ArrowDown, ArrowUp, Plus, Trash2, Undo2 } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { queryKeys } from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import { useSummaryExamplesQuery } from "@/api/summaryExamples";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import type { DraftPart } from "@/lib/assetContent";
import {
  addExamplePair,
  exampleLanguages,
  exampleView,
  examplesPartIndex,
  findExamplesPart,
  moveExamplePair,
  refuseRemoval,
  refuseShortSet,
  removeExampleDocument,
  removeExamplePair,
  restoreExamplePart,
  seedAuthoredItems,
  updateExamplePair,
  writeExampleDocument,
  type SummaryExampleOrigin,
  type SummaryExamplePair,
  type SummaryExampleRow,
  type SummaryExampleView,
} from "@/lib/summaryExamples";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import { Label, Textarea } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { Tooltip } from "@/ui/Tooltip";
import { LoadingState } from "@/ui/states";

/**
 * The ordered input/output example pairs of one Summary prompt (#916).
 *
 * ## WHAT THE REFERENCE HAS, AND WHAT THIS ADDS TO IT
 *
 * [`/library/summary/diagnostic_findings`](https://noproductcontent.lovable.app/library/summary/diagnostic_findings)
 * is a named prompt field with Back, Details and Save, and it has no example
 * editor at all — which is the observed gap this leaf exists to close: the exact
 * MOD and NII example sets cannot be reconstructed from a prompt field. So the
 * page keeps its heading, its target line, its Details strip, its one prompt
 * field and its Save/Discard pair EXACTLY as #863 built them, and this panel is
 * added underneath, using the same compact row idiom, the same Move up / Move
 * down / Remove trio the Pathway step list ships, and the same one Save.
 *
 * It is one part of the SAME draft. Nothing here publishes on its own: an edit
 * writes the `<language>/examples` structured part of the open draft, and the
 * page's existing Save publishes the whole parts array as the next immutable
 * version. That is what makes a prompt edit and an example edit one atomic
 * clinical change, and it is also why unsaved pairs survive a prompt edit, a
 * refused Save and a stale-save recovery for free — they live in the draft
 * `useNamedDraft` already holds across all three.
 *
 * ## THE PAIRS ON SCREEN ARE THE GENERATION CONTEXT
 *
 * `items[i]` becomes `examples/example_{i+1}/{input,output}.txt`, and the pinned
 * summary agent turns each pair into a user/assistant turn ahead of the real
 * request, ordered by the integer the directory name ends with. So the position
 * number beside each row is not decoration and not a filename a clinician may
 * edit: it IS the order those turns reach the model. It is shown, it is
 * changed only by the reorder controls, and it is never typed.
 *
 * ## THREE EDITS, BECAUSE THE PINNED LOADER EXPRESSES THREE
 *
 * `list_clinical_dir` unions the base pack's example directories with the
 * overlay's and there is no tombstone, so an authored run of N replaces base
 * position by position and every base index above N survives into the runtime
 * set. Replacement, addition and reordering are therefore expressible; removing
 * an inherited pair is not, and this panel refuses that press BEFORE it changes
 * the draft — the ticket's "unchanged persisted/effective state" is a
 * requirement about the moment of the refusal, not only about its wording.
 * Resetting the whole set is the supported way back to inheritance, and it is a
 * separate control with a separate sentence.
 *
 * WHAT IS NEVER OFFERED: emptying an output as a way to delete a pair. An empty
 * expected output is base canon's own `patient_additions/example_1/output.txt`
 * and a valid teaching example — "for this conversation shape, emit nothing" —
 * so the field says that is what an empty value means, and nothing on this
 * surface treats it as absence.
 */

/** What each origin is called, and what it means for the runtime. */
const ORIGIN_LABELS: Record<SummaryExampleOrigin, string> = {
  inherited: "From the base library",
  replaced: "Replaces a base example",
  added: "Added here",
};

const ORIGIN_HINTS: Record<SummaryExampleOrigin, string> = {
  inherited:
    "This pair is the base clinical library's own, carried through unchanged. Editing it replaces the base pair at this position.",
  replaced:
    "The base clinical library ships a pair at this position and this one is written over it. The base pair is not deleted; the authored file wins at the same index.",
  added:
    "This pair is beyond every position the base clinical library occupies, so it is an addition of this version's own.",
};

/** What an empty expected output means, wherever it has to be described. */
const EMPTY_OUTPUT = "the model is taught to answer with nothing here";

export function SummaryExamplesPanel({
  assetId,
  version,
  parts,
  readOnly,
  onChange,
}: {
  assetId: string;
  /** The version the draft is pinned to — what provenance is read against. */
  version: string;
  parts: readonly DraftPart[];
  readOnly: boolean;
  onChange: (parts: DraftPart[]) => void;
}) {
  const languages = exampleLanguages(parts);

  return (
    <section
      aria-label="Examples"
      data-slot="summary-examples"
      className="space-y-3 rounded-md border border-border bg-muted/20 px-4 py-3"
    >
      <div>
        <h2 className="text-sm font-semibold text-foreground">Examples</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Ordered input and expected-output pairs. The pinned summary agent
          replays them in this order, before the real request, to teach this
          prompt what an answer looks like. An empty expected output is a valid
          example — it teaches the model to answer with nothing.
        </p>
      </div>

      {languages.length === 0 ? (
        // A prompt stored under a bare, language-neutral key. Its examples
        // would compose against a `{lang}`-templated directory and the service
        // refuses that key at the key the client sent, so there is nothing to
        // offer here — stated rather than rendered as an empty editor.
        <p className="text-sm text-muted-foreground">
          This prompt&apos;s documents carry no language, and an example set is
          stored per pack language. Example pairs cannot be authored on it.
        </p>
      ) : (
        languages.map((language) => (
          <SummaryExamplesLanguage
            key={language}
            assetId={assetId}
            version={version}
            language={language}
            showLanguage={languages.length > 1}
            parts={parts}
            readOnly={readOnly}
            onChange={onChange}
          />
        ))
      )}
    </section>
  );
}

function SummaryExamplesLanguage({
  assetId,
  version,
  language,
  showLanguage,
  parts,
  readOnly,
  onChange,
}: {
  assetId: string;
  version: string;
  language: string;
  showLanguage: boolean;
  parts: readonly DraftPart[];
  readOnly: boolean;
  onChange: (parts: DraftPart[]) => void;
}) {
  const read = useSummaryExamplesQuery(assetId, version, language);
  const retry = useRetryPausedQuery(
    queryKeys.summaryExamples(assetId, version, language),
  );
  const view = exampleView(parts, language, read.data);

  // The PART a Reset took out of the draft, and where it sat, so the press is
  // reversible without the page-level Discard — which would also throw away an
  // unsaved prompt edit.
  //
  // THE PART ITSELF, NOT THE PAIRS IT RENDERED AS. `parts` is an ordered list
  // by contract and `isSameDraft` compares it position by position, so an undo
  // that re-derived the document would put the set back after the prompt when
  // it was stored before it — leaving the draft dirty and Save offering to
  // publish a version nobody edited. Held with the version it was pressed at
  // and dropped when that moves, which is the render-time adjustment
  // `useNamedDraft` uses for the same shape of problem: after the Save lands,
  // the set really is inherited and the banner has nothing left to say.
  const [pendingReset, setPendingReset] = useState<{
    version: string;
    part: DraftPart;
    index: number;
  } | null>(null);
  if (pendingReset !== null && pendingReset.version !== version) {
    setPendingReset(null);
  }
  // Undoing a Reset puts the part back, so the banner is only the state where
  // the draft still has no set of its own.
  const resetPending = pendingReset !== null && !view.authored;

  // The refusal a removal press produced, which must survive until the next
  // action rather than flashing: it is the whole evidence that nothing changed.
  const [refusal, setRefusal] = useState<string | null>(null);

  const unread = read.isPaused || read.isError;
  const failure = read.isPaused ? read.failureReason : read.error;

  function edit(items: SummaryExamplePair[]) {
    setRefusal(null);
    setPendingReset(null);
    onChange(writeExampleDocument(parts, language, items));
  }

  function reset() {
    const part = findExamplesPart(parts, language);
    if (part === null) {
      // Nothing of this version's own to drop. Unreachable — the control is
      // rendered only for an authored set — and a silent no-op rather than a
      // banner promising to undo something that was never taken out.
      return;
    }
    setRefusal(null);
    setPendingReset({
      version,
      part,
      index: examplesPartIndex(parts, language),
    });
    onChange(removeExampleDocument(parts, language));
  }

  const body = (
    <>
      {read.isPending ? (
        <LoadingState label="Loading example pairs" rows={2} />
      ) : unread ? (
        // FAIL CLOSED ON AN UNREAD SET, and this is the one place in this panel
        // that is stricter than "render what we have". The floor an authored
        // set may not go below is the SERVICE's answer; without it this surface
        // cannot tell an addition from a truncation, and an editor that let a
        // clinician author against an unknown floor would produce exactly the
        // silent inherited-pair leak the whole ticket is about. So the pairs
        // this version carries are still shown — they are stored content and
        // hiding them would be its own lie — and authoring is withheld with the
        // reason and a retry.
        <>
          <Notice
            tone="warning"
            action={
              isTerminalQueryError(failure) ? undefined : (
                <Button variant="outline" size="sm" onClick={retry}>
                  Try again
                </Button>
              )
            }
          >
            The Editor Service did not answer which example pairs this target
            inherits from the base clinical library, so pairs cannot be edited
            here yet. What this version stores is shown below and is unchanged.
          </Notice>
          {/* ONLY WHAT THIS VERSION ACTUALLY CARRIES. `ExampleRows`' empty
              state says the target has no pairs, and in a degraded read that
              is precisely the thing this branch does not know — the inherited
              half is exactly what could not be established. So an empty set
              renders as the notice alone. */}
          {view.rows.length > 0 ? (
            <ExampleRows
              view={view}
              readOnly
              onMove={() => undefined}
              onRemove={() => undefined}
              onEdit={() => undefined}
            />
          ) : null}
        </>
      ) : !view.baseAvailable ? (
        // The service answered, and its answer is that this deployment has no
        // readable base pack. `validate_summary_examples` refuses an authored
        // set with 503 in that state rather than storing one it could not check,
        // so offering the controls would be offering a Save that cannot land.
        <>
          <Notice tone="warning">
            This deployment has no readable base clinical library mounted, so
            what a Summary target inherits cannot be established and example
            pairs cannot be authored. What this version stores is shown below
            and is unchanged.
          </Notice>
          {/* ONLY WHAT THIS VERSION ACTUALLY CARRIES. `ExampleRows`' empty
              state says the target has no pairs, and in a degraded read that
              is precisely the thing this branch does not know — the inherited
              half is exactly what could not be established. So an empty set
              renders as the notice alone. */}
          {view.rows.length > 0 ? (
            <ExampleRows
              view={view}
              readOnly
              onMove={() => undefined}
              onRemove={() => undefined}
              onEdit={() => undefined}
            />
          ) : null}
        </>
      ) : resetPending ? (
        <Notice
          tone="warning"
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const held = pendingReset;
                if (held !== null) {
                  onChange(restoreExamplePart(parts, held.part, held.index));
                }
                setPendingReset(null);
              }}
            >
              <Undo2 aria-hidden="true" className="h-4 w-4" />
              Undo reset
            </Button>
          }
        >
          {view.inheritedCount > 0
            ? `Saving now drops this version's own example set, and this target inherits the base clinical library's ${view.inheritedCount} ${view.inheritedCount === 1 ? "pair" : "pairs"} again.`
            : "Saving now drops this version's own example set. The base clinical library supplies no pairs at this target, so this prompt would then teach by its text alone."}
        </Notice>
      ) : (
        <>
          {/* THE REFUSAL, and it is `role="alert"` because it is the answer to a
              press the clinician just made and nothing else on screen moved. */}
          {refusal === null ? null : (
            <Notice tone="destructive" role="alert">
              {refusal}
            </Notice>
          )}

          <ShortSetNotice view={view} />

          <ExampleRows
            view={view}
            readOnly={readOnly}
            onMove={(index, to) =>
              edit(moveExamplePair(seedAuthoredItems(view), index, to))
            }
            onRemove={(row) => {
              const why = refuseRemoval(view, row);
              if (why !== null) {
                // BEFORE THE MUTATION. Nothing is written to the draft, so the
                // set on screen, the stored version and the effective set are
                // all exactly what they were.
                setRefusal(why);
                return;
              }
              edit(removeExamplePair(seedAuthoredItems(view), row.index));
            }}
            onEdit={(index, half, text) =>
              edit(updateExamplePair(seedAuthoredItems(view), index, half, text))
            }
          />

          {readOnly ? null : (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => edit(addExamplePair(seedAuthoredItems(view)))}
              >
                <Plus aria-hidden="true" className="h-4 w-4" />
                Add example pair
              </Button>
              {view.authored ? (
                <Tooltip
                  content={
                    view.inheritedCount > 0
                      ? `Drop this version's own set and inherit the base clinical library's ${view.inheritedCount} ${view.inheritedCount === 1 ? "pair" : "pairs"} again.`
                      : "Drop this version's own set. The base clinical library supplies no pairs at this target."
                  }
                >
                  <Button variant="outline" size="sm" onClick={reset}>
                    <Undo2 aria-hidden="true" className="h-4 w-4" />
                    Reset to the base library
                  </Button>
                </Tooltip>
              ) : null}
            </div>
          )}
        </>
      )}
    </>
  );

  if (!showLanguage) {
    return <div className="space-y-3">{body}</div>;
  }
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Language: {language}
      </h3>
      {body}
    </div>
  );
}

/** The set-level refusal a stored set that has fallen behind base produces. */
function ShortSetNotice({ view }: { view: SummaryExampleView }) {
  const message = refuseShortSet(view);
  return message === null ? null : <Notice tone="warning">{message}</Notice>;
}

/**
 * The ordered pairs, with their position, provenance and controls.
 *
 * ONE `<ol>`, because the order is the content. A list whose order can be
 * changed needs the order announced when it changes, which is `PathwayStepList`'s
 * pattern applied to a list whose members have no names: a step announces "Chief
 * complaint moved to position 2", and an example pair can only announce its
 * position, so the live region says exactly that and nothing it cannot know.
 */
function ExampleRows({
  view,
  readOnly,
  onMove,
  onRemove,
  onEdit,
}: {
  view: SummaryExampleView;
  readOnly: boolean;
  onMove: (index: number, to: number) => void;
  onRemove: (row: SummaryExampleRow) => void;
  onEdit: (index: number, half: "input" | "output", text: string) => void;
}) {
  // Which control should hold focus once the moved order has rendered. A REF
  // rather than state, for `PathwayStepList`'s reason: nothing renders from it,
  // it is consumed once by the effect below, and clearing it in state would be
  // a second render whose only job is to forget the instruction.
  const restore = useRef<{ index: number; direction: "up" | "down" } | null>(
    null,
  );
  const [announcement, setAnnouncement] = useState("");
  const controls = useRef(new Map<string, HTMLButtonElement>());
  const count = view.rows.length;

  useEffect(() => {
    const pending = restore.current;
    if (pending === null) {
      return;
    }
    restore.current = null;
    // ALWAYS, unlike `PathwayStepList`, and the difference is the key. A step
    // row is keyed on its ROLE, so a move relocates the existing DOM node and
    // the browser's focus rides along; that list only intervenes when the
    // pressed control has become inert. A PAIR has no identity apart from its
    // position, so these rows are keyed on the position — React keeps the DOM
    // node where it is and rewrites its contents, which leaves the focused
    // button belonging to a DIFFERENT pair than the one the clinician just
    // moved. Focus therefore has to follow the pair to the position it landed
    // at, every time.
    const landed = pending.index;
    if (landed < 1 || landed > count) {
      // The row left the list between the move and this effect — somebody
      // else's version landed and re-seeded the draft. Guessing a neighbour
      // would move focus somewhere the clinician did not ask for.
      return;
    }
    const stillOperable =
      pending.direction === "up" ? landed > 1 : landed < count;
    // The same direction while it can still be pressed — a clinician moving a
    // pair up three positions presses one button three times — and the pair's
    // other direction once it cannot, which is the operable half of the pair
    // and the control that undoes the move.
    const direction = stillOperable
      ? pending.direction
      : pending.direction === "up"
        ? "down"
        : "up";
    controls.current.get(`${landed}:${direction}`)?.focus();
  }, [count, view.rows]);

  function move(row: SummaryExampleRow, direction: "up" | "down") {
    const to = direction === "up" ? row.index - 1 : row.index + 1;
    restore.current = { index: to, direction };
    setAnnouncement(`Example moved to position ${to} of ${count}.`);
    onMove(row.index, to);
  }

  if (count === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        This target has no example pairs. The prompt teaches by its text alone
        until one is added.
      </p>
    );
  }

  return (
    <>
      {/* Mounted unconditionally and empty until a move happens: a live region
          added to the DOM at the same moment it gains text is frequently not
          announced at all. */}
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
      <ol
        aria-label="Example pairs, in the order the summary agent replays them"
        className="space-y-3"
      >
        {view.rows.map((row) => (
          <ExampleRow
            // Keyed on the POSITION, which is the pair's whole identity: a pair
            // has no stable id in the stored document, and keying on the text
            // would hand React two identical keys the moment two pairs shared
            // an input — including while one of them is focused.
            key={row.index}
            row={row}
            count={count}
            readOnly={readOnly}
            registerControl={(direction, element) => {
              const key = `${row.index}:${direction}`;
              if (element) controls.current.set(key, element);
              else controls.current.delete(key);
            }}
            onMove={(direction) => move(row, direction)}
            onRemove={() => onRemove(row)}
            onEdit={(half, text) => onEdit(row.index, half, text)}
          />
        ))}
      </ol>
    </>
  );
}

function ExampleRow({
  row,
  count,
  readOnly,
  registerControl,
  onMove,
  onRemove,
  onEdit,
}: {
  row: SummaryExampleRow;
  count: number;
  readOnly: boolean;
  registerControl: (
    direction: "up" | "down",
    element: HTMLButtonElement | null,
  ) => void;
  onMove: (direction: "up" | "down") => void;
  onRemove: () => void;
  onEdit: (half: "input" | "output", text: string) => void;
}) {
  const inputId = useId();
  const outputId = useId();
  const outputHintId = useId();

  return (
    <li className="rounded-md border border-border bg-card px-3 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold tabular-nums text-muted-foreground">
          {row.index}.
        </span>
        <Tooltip content={ORIGIN_HINTS[row.origin]}>
          <Badge tone="outline">{ORIGIN_LABELS[row.origin]}</Badge>
        </Tooltip>
        <span className="flex-1" />
        {readOnly ? null : (
          <div className="flex items-center gap-0.5">
            {/* THE POSITION IS IN EVERY LABEL, unlike the Pathway step list's
                — and for the same underlying reason, applied to a list whose
                members have no names. A step is "Move Chief complaint up"; an
                example pair has nothing but its position to be identified by,
                so six unlabelled "Move up" controls would be the repeated
                ambiguous buttons the accessibility audit names. The label
                changes when the pair moves, which is correct here: the control
                genuinely became a different row's. */}
            <IconButton
              ref={(element) => registerControl("up", element)}
              label={`Move example ${row.index} up`}
              icon={<ArrowUp className="h-4 w-4" />}
              disabled={row.index === 1}
              onClick={() => onMove("up")}
            />
            <IconButton
              ref={(element) => registerControl("down", element)}
              label={`Move example ${row.index} down`}
              icon={<ArrowDown className="h-4 w-4" />}
              disabled={row.index === count}
              onClick={() => onMove("down")}
            />
            <IconButton
              label={`Remove example ${row.index}`}
              icon={<Trash2 className="h-4 w-4" />}
              onClick={onRemove}
            />
          </div>
        )}
      </div>

      <div className="mt-2 grid gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor={inputId} className="text-xs">
            Input
          </Label>
          {/* `dir="auto"` rather than a fixed direction: the source sets are
              Hebrew and this editor also serves the English tree, so the
              browser reads each value's own first strong character instead of
              this file deciding for both. */}
          <Textarea
            id={inputId}
            dir="auto"
            rows={6}
            value={row.input}
            readOnly={readOnly}
            onChange={(event) => onEdit("input", event.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={outputId} className="text-xs">
            Expected output
          </Label>
          <Textarea
            id={outputId}
            dir="auto"
            rows={6}
            value={row.output}
            readOnly={readOnly}
            {...(row.output.length === 0
              ? { "aria-describedby": outputHintId }
              : {})}
            // The empty state is a REAL expected output, so the field says what
            // an empty value means rather than asking to be filled in.
            placeholder="Empty — the model is taught to answer with nothing"
            onChange={(event) => onEdit("output", event.target.value)}
          />
          {/* ON THE EMPTY ROW ONLY. The rule itself is stated once, in the
              panel's own description; repeating it under every filled field
              would put the same sentence on screen N times and say nothing
              about the row it sits under. What a row DOES need to say is what
              its own empty value means — a deliberate teaching example rather
              than an unfinished field or a way to delete the pair. */}
          {row.output.length === 0 ? (
            <p id={outputHintId} className="text-xs text-muted-foreground">
              Empty: {EMPTY_OUTPUT}. That is a valid example and is not a way to
              remove this pair.
            </p>
          ) : null}
        </div>
      </div>
    </li>
  );
}
